"use client";

import { BUTTON_SECONDARY, BUTTON_SMALL } from "@/components/tool-kit/styles";
import { QuantityField } from "@/components/tools/physics-waves/quantity-field";
import { t } from "@/lib/i18n/interpolate";
import type { UnitDef } from "@/lib/tool-kit/physics-waves/units";

export interface ValueRow {
  id: number;
  value: string;
  unit: string;
}

export interface RowListStrings {
  rowLabel: string;
  unitFor: string;
  remove: string;
  add: string;
}

const MIN_ROWS = 2;
const MAX_ROWS = 12;

/** A growing list of value-and-unit rows: the resistors or capacitors being combined. */
export function RowList({
  idPrefix,
  rows,
  onChange,
  units,
  defaultUnit,
  nextId,
  strings,
}: {
  idPrefix: string;
  rows: readonly ValueRow[];
  onChange: (rows: ValueRow[]) => void;
  units: readonly UnitDef[];
  defaultUnit: string;
  nextId: () => number;
  strings: RowListStrings;
}) {
  const update = (id: number, patch: Partial<ValueRow>) =>
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {rows.map((row, index) => {
          const label = t(strings.rowLabel, { n: index + 1 });
          return (
            <div className="space-y-1" key={row.id}>
              <QuantityField
                id={`${idPrefix}-${row.id}`}
                label={label}
                onChange={(value) => update(row.id, { value })}
                onUnitChange={(unit) => update(row.id, { unit })}
                unit={row.unit}
                unitLabel={t(strings.unitFor, { name: label })}
                units={units}
                value={row.value}
              />
              {rows.length > MIN_ROWS ? (
                <button
                  className={BUTTON_SMALL}
                  onClick={() => onChange(rows.filter((r) => r.id !== row.id))}
                  type="button"
                >
                  {t(strings.remove, { n: index + 1 })}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      {rows.length < MAX_ROWS ? (
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            onChange([...rows, { id: nextId(), value: "", unit: defaultUnit }])
          }
          type="button"
        >
          {strings.add}
        </button>
      ) : null}
    </div>
  );
}
